"use client";

import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {formatDateTime, formatTime} from "@/libs/helper";
import {ManageBookingItem} from "@/types/booking";
import {FiClock, FiNavigation} from "react-icons/fi";

export type PassengerStatus = "Paid" | "Unpaid";

type PassengerDetail = {
  id: string;
  name: string;
  image: string;
  status: PassengerStatus;
  boardingPoint: string;
  dropPoint: string;
  reason: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  travelledTime: string;
};

export type BookingUiDetail = {
  seatsFilled: number;
  totalSeats: number;
  routeName: string;
  busStartTime: string;
  busEndTime: string;
  totalTravelTime: string;
  liveStatus: string;
  liveTrackingAvailable: boolean;
  passengers: PassengerDetail[];
};

export const getManageBookingFallbackRows = (statusType?: number) => {
  switch (statusType) {
    case 1:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 2001,
          vehicleName: "TN01A9934",
          bookingTime: new Date("2026-05-28T09:00:00").getTime(),
          driverName: "John Driver",
          driverEmpId: "SAMP062",
          pickupPoint: "KG Cinemas Stop",
          dropPoint: "Test Institute Branch 1"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 2002,
          vehicleName: "TN38DE2312",
          bookingTime: new Date("2026-05-28T10:00:00").getTime(),
          driverName: "Arun Driver",
          driverEmpId: "SAMP063",
          pickupPoint: "Hope College",
          dropPoint: "NeoSay Campus"
        }
      ];
    case 2:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 3001,
          vehicleName: "TN01AB1234",
          bookingTime: new Date("2026-06-01T09:00:00").getTime(),
          driverName: "Kumar Driver",
          driverEmpId: "SAMP070",
          pickupPoint: "Gandhipuram",
          dropPoint: "NeoSay Campus"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 3002,
          vehicleName: "TN38XY5678",
          bookingTime: new Date("2026-06-01T10:30:00").getTime(),
          driverName: "Ravi Driver",
          driverEmpId: "SAMP071",
          pickupPoint: "Peelamedu",
          dropPoint: "Airport"
        }
      ];
    case 3:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 3001,
          vehicleName: "TN09Z4455",
          bookingTime: new Date("2026-05-27T08:30:00").getTime(),
          driverName: "Vignesh Driver",
          driverEmpId: "SAMP071",
          pickupPoint: "Saibaba Colony",
          dropPoint: "NeoSay Campus"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 3002,
          vehicleName: "TN66BY2295",
          bookingTime: new Date("2026-05-27T18:00:00").getTime(),
          driverName: "Kumar Driver",
          driverEmpId: "SAMP072",
          pickupPoint: "Town Hall",
          dropPoint: "Saravanampatti"
        }
      ];
    case 4:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 4001,
          vehicleName: "TN07AB1200",
          bookingTime: new Date("2026-05-26T09:30:00").getTime(),
          driverName: "Raja Driver",
          driverEmpId: "SAMP081",
          pickupPoint: "Gandhipuram",
          dropPoint: "Peelamedu",
          comments: "Booking cancelled by user request"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 4002,
          vehicleName: "TN11CD3400",
          bookingTime: new Date("2026-05-26T17:45:00").getTime(),
          driverName: "Mani Driver",
          driverEmpId: "SAMP082",
          pickupPoint: "Ukkadam",
          dropPoint: "Singanallur",
          comments: "Trip cancelled due to route change"
        }
      ];
    case 5:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 5001,
          vehicleName: "TN01AB1234",
          bookingTime: new Date("2026-05-28T09:00:00").getTime(),
          driverName: "Kumar Driver",
          driverEmpId: "SAMP080",
          pickupPoint: "Gandhipuram",
          dropPoint: "NeoSay Campus"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 5002,
          vehicleName: "TN38XY5678",
          bookingTime: new Date("2026-05-28T10:30:00").getTime(),
          driverName: "Ravi Driver",
          driverEmpId: "SAMP081",
          pickupPoint: "Peelamedu",
          dropPoint: "Airport"
        }
      ];
    default:
      return [];
  }
};

const formatInputDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getBookingUiDetail = (
  booking: ManageBookingItem,
  statusType?: number
): BookingUiDetail => {
  const seed = Number(booking.bookingId || 0);
  const totalSeats = 24;
  const seatsFilled = 10 + (seed % 11);
  const routeName = `Route-${String((seed % 12) + 1).padStart(2, "0")}`;
  const busStartTime = formatDateTime(booking.bookingTime);
  const busEndTime = formatDateTime(booking.bookingTime + 45 * 60 * 1000);
  const totalTravelTime = `${25 + (seed % 21)} mins`;
  const liveTrackingAvailable = seed % 4 !== 0;
  const liveStatus = !liveTrackingAvailable
    ? "Tracking unavailable"
    : seatsFilled >= 22
      ? "Near full"
      : seatsFilled >= 16
        ? "Healthy load"
        : "Comfortable load";

  const passengers: PassengerDetail[] = Array.from({length: 5}, (_, index) => {
    const isPaid = (seed + index) % 3 !== 0;
    const reason =
      statusType === 4
        ? index === 0 && booking.comments
          ? booking.comments
          : "Booking cancelled by user request"
        : statusType === 5
          ? index === 0 && booking.pickupMissed
            ? `Pickup missed at ${formatTime(booking.pickupMissed)}`
            : "Passenger did not board"
          : isPaid
            ? "Fare cleared successfully"
            : "Payment pending";

    return {
      id: `${booking.bookingId}-${index + 1}`,
      name:
        index === 0
          ? booking.name
          : `Passenger ${String.fromCharCode(65 + ((seed + index) % 26))}${index + 1}`,
      image:
        index === 0 && booking.image && booking.image.trim() !== ""
          ? booking.image
          : "/images/avatar.png",
      status: isPaid ? "Paid" : "Unpaid",
      boardingPoint:
        index === 0
          ? booking.pickupPoint
          : `${booking.pickupPoint} Stop ${index + 1}`,
      dropPoint:
        index === 0
          ? booking.dropPoint
          : `${booking.dropPoint} Gate ${index + 1}`,
      reason,
      travelFrom: formatTime(booking.bookingTime + index * 4 * 60 * 1000),
      travelTo: formatTime(
        booking.bookingTime + (index * 4 + 12 + index * 5) * 60 * 1000
      ),
      travelDate: formatInputDate(booking.bookingTime + index * 4 * 60 * 1000),
      travelledTime: `${12 + index * 5} mins`
    };
  });

  return {
    seatsFilled,
    totalSeats,
    routeName,
    busStartTime,
    busEndTime,
    totalTravelTime,
    liveStatus,
    liveTrackingAvailable,
    passengers
  };
};

export const ManageBookingDetailView = ({
  booking,
  statusType,
  backHref,
  onClose
}: {
  booking: ManageBookingItem;
  statusType: number;
  backHref?: string;
  onClose?: () => void;
}) => {
  const uiDetail = getBookingUiDetail(booking, statusType);
  const isOngoing = statusType === 1;
  const isCancelled = statusType === 4;
  const filteredPassengers = uiDetail.passengers;

  const passengerTable = {
    columns: [
      {uid: "image", name: "Photo"},
      {uid: "name", name: "Name"},
      {uid: "id", name: "Booking ID"},
      {uid: "status", name: "Status"},
      {uid: "boardingPoint", name: "Boarding Point"},
      {uid: "dropPoint", name: "Drop Point"},
      {uid: "travelDate", name: "Travel Date"},
      ...(isCancelled ? [{uid: "reason", name: "Reason"}] : []),
      {uid: "travel", name: "Travel"}
    ],
    activeColumns: [] as string[],
    tabledatas: filteredPassengers.map(passenger => ({
      ...passenger,
      travel: `${passenger.travelFrom} - ${passenger.travelTo} | ${passenger.travelledTime}`
    })),
    style: {
      height: "h-[320px]"
    },
    meta: {
      id: 0,
      title: "",
      description: "",
      hasSearch: false,
      hasUpload: false,
      hasDownload: false,
      hasAdd: false,
      hasActions: false,
      hasCheckbox: false,
      hasFilter: false,
      hasHeader: false,
      hasFooter: false,
      hasPagination: false,
      hasCreate: false
    },
    url: {
      page: {},
      api: {
        get: ""
      }
    }
  };

  passengerTable.activeColumns = passengerTable.columns.map(
    column => column.uid
  );

  return (
    <div className="flex min-h-[calc(100vh-63px)] flex-col px-3 py-3">
      <div className=" pb-3">
        <div className="font-bold">Booking Details</div>
        <div className="mt-2  font-semibold ">{booking.vehicleName}</div>
      </div>

      <div className="mt-2 flex flex-row gap-2">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="font-semibold text-black">Trip Overview</div>
          <div className="mt-2 flex flex-row gap-2">
            <div className="rounded-xl bg-white px-2 py-2">
              <div className="flex flex-row items-center gap-2  text-xs font-medium">
                <FiClock className="h-3.5 w-3.5" />
                <span>Trip Timing</span>
              </div>
              <div className="mt-2 text-sm ">
                {uiDetail.busStartTime} - {uiDetail.busEndTime}
              </div>
            </div>
            <div className="rounded-xl bg-white px-2 py-2">
              <div className="flex items-center gap-2 text-xs font-medium">
                <FiNavigation className="h-3.5 w-3.5" />
                <span>Route</span>
              </div>
              <div className="mt-2 text-sm">
                {booking.pickupPoint} {"->"} {booking.dropPoint}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200  p-4">
          <div className="font-semibold">Capacity & Timing</div>
          <div className="mt-2 flex flex-row gap-2">
            <div className="rounded-xl bg-blue-50 px-2 py-2">
              <div className="text-xs font-medium text-blue-400">
                Seat Filled
              </div>
              <div className="mt-2 text-sm font-semibold text-blue-700">
                {uiDetail.seatsFilled}/{uiDetail.totalSeats}
              </div>
            </div>
            <div className="rounded-xl bg-emerald-50 px-2 py-2">
              <div className="text-xs font-medium text-emerald-400">
                {isOngoing ? "Live Tracking" : "Route View"}
              </div>
              <div className="mt-2 text-sm font-semibold text-emerald-700">
                {isOngoing
                  ? uiDetail.liveTrackingAvailable
                    ? "Active"
                    : "Unavailable"
                  : "Travelled route line"}
              </div>
            </div>
            <div className="rounded-xl bg-white px-2 py-2">
              <div className="text-xs font-medium">Total Bus Travel Time</div>
              <div className="mt-2  text-sm font-semibold ">
                {uiDetail.totalTravelTime}
              </div>
            </div>
            <div className="rounded-xl bg-white px-2 py-2">
              <div className="text-xs font-medium">Driver</div>
              <div className="mt-2 text-sm font-semibold">
                {booking.driverName} ({booking.driverEmpId})
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
          <div className="h-[320px] w-full">
            <NeoAGgrid table={passengerTable} />
          </div>
        </div>
      </div>
    </div>
  );
};
